import cn from 'classnames';
import React, { useRef, useState, type JSX } from 'react';
import Arrow from '../../visuals/Icons/Arrow';

export interface IAccordionItem {
  title?: string | JSX.Element;
  isToggle?: boolean;
  variant?: string;
  children: React.ReactNode;
  withMultipleScrolls?: boolean;
  preventScroll?: boolean;
}

const AccordionItem: React.FC<IAccordionItem> = ({
  title,
  isToggle = false,
  variant = 'primary',
  children,
  withMultipleScrolls = false,
  preventScroll = false,
}) => {
  const [isOpen, setIsOpen] = useState(isToggle);
  const accordionItemRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = (currentRef) => {
    currentRef?.scrollIntoView({
      behavior: 'smooth',
      block: withMultipleScrolls ? 'nearest' : undefined,
      inline: 'start',
    });
  };

  const handleToggle = () =>
    setIsOpen((wasOpen) => {
      if (!wasOpen && !preventScroll) {
        // wait for next render to scroll, so the accordion is expanded
        requestAnimationFrame(() => handleScroll(accordionItemRef.current));
      }

      return !wasOpen;
    });

  return (
    <div
      className={cn('tw-ml-2', variant !== 'primary' && 'tw-bg-[#f9f9f9]')}
      ref={accordionItemRef}
    >
      <div
        className={cn(
          [
            'tw-relative tw-flex tw-cursor-pointer tw-flex-row tw-items-center',
            'tw-justify-between tw-border-0 tw-border-b',
            'tw-border-solid tw-border-[#f5f5f5] tw-px-3 tw-py-6',
            'tw-transition-all tw-duration-300 tw-ease-out',
            'after:tw-absolute after:tw-inset-[6px] after:tw-left-[-9px]',
            "after:tw-rounded-lg after:tw-bg-yellow-600 after:tw-content-['']",
          ],
          variant === 'primary'
            ? 'desktop:hover:tw-bg-beige-200'
            : 'desktop:hover:tw-bg-[#e0dbd5]',
          isOpen ? 'after:tw-w-[9px]' : 'after:tw-w-0',
        )}
        onClick={handleToggle}
      >
        {typeof title === 'string' ? (
          <span className={cn('tw-text-base', isOpen && 'tw-font-extrabold')}>
            {title}
          </span>
        ) : (
          title
        )}
        <div
          className={cn(
            'tw-ml-2 tw-transition-all tw-duration-300 tw-ease-out',
            isOpen ? 'tw-rotate-180' : 'tw-rotate-0',
          )}
        >
          <Arrow width={12} height={12} headDirection="bottom" aria-hidden />
        </div>
      </div>
      <div
        className={cn(
          'tw-transition-all tw-duration-300 tw-ease-out',
          isOpen
            ? 'tw-block tw-max-h-[500rem] tw-opacity-100'
            : 'tw-hidden tw-max-h-[0] tw-opacity-0',
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default AccordionItem;
